'use client';

import { c as _c } from "react/compiler-runtime";
import { type Context, createContext, use } from 'react';
import { getConsensusChartPlaybackDurationMs } from '@/components/support-mainnet/how-it-works/consensus-builds-chart-engine';
import { getDebateSettledPlaybackDurationMs } from '@/components/support-mainnet/how-it-works/debate-settled-animation-engine';
import { getDebateStartsPlaybackDurationMs } from '@/components/support-mainnet/how-it-works/debate-starts-animation-engine';
export const HOW_IT_WORKS_STEP_COUNT = 3;
export type HowItWorksStepIndex = 0 | 1 | 2;
export type HowItWorksStepPlaybackGenerations = readonly [number, number, number];
export const INITIAL_HOW_IT_WORKS_STEP_PLAYBACK_GENERATIONS: HowItWorksStepPlaybackGenerations = [0, 0, 0];
type HowItWorksStepPlaybackContextValue = {
  activeStep: number | null;
  hoverPlayStep: number | null;
  playingStep: number | null;
  stepPlaybackGenerations: HowItWorksStepPlaybackGenerations;
  requestStepPlayback: (stepIndex: HowItWorksStepIndex) => void;
};
export const HowItWorksStepPlaybackContext: Context<HowItWorksStepPlaybackContextValue | null> = createContext<HowItWorksStepPlaybackContextValue | null>(null);
export const HOW_IT_WORKS_SECTION_INTERSECTION_THRESHOLD = 0.25;
export function getHowItWorksStepDurationMs(stepIndex: HowItWorksStepIndex): number {
  switch (stepIndex) {
    case 0:
      return getDebateStartsPlaybackDurationMs();
    case 1:
      return getConsensusChartPlaybackDurationMs();
    case 2:
      return getDebateSettledPlaybackDurationMs();
    default:
      {
        const exhaustive: never = stepIndex;
        return exhaustive;
      }
  }
}
function useHowItWorksStepPlaybackContext(): HowItWorksStepPlaybackContextValue | null {
  return use(HowItWorksStepPlaybackContext);
}
export function useHowItWorksOrchestrationActive() {
  return useHowItWorksStepPlaybackContext() !== null;
}
export function useHowItWorksPlaybackGeneration(stepIndex) {
  const context = useHowItWorksStepPlaybackContext();
  return context?.stepPlaybackGenerations[stepIndex] ?? 0;
}
export function useHowItWorksPlayingStep() {
  const context = useHowItWorksStepPlaybackContext();
  return context?.playingStep ?? null;
}
export function useHowItWorksRequestStepPlayback() {
  const $ = _c(2);
  const context = useHowItWorksStepPlaybackContext();
  let t0;
  if ($[0] !== context?.requestStepPlayback) {
    t0 = context?.requestStepPlayback ?? _temp;
    $[0] = context?.requestStepPlayback;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function _temp() {}
export function useHowItWorksStepShouldPlay(stepIndex) {
  const context = useHowItWorksStepPlaybackContext();
  if (context === null) {
    return false;
  }
  if (context.hoverPlayStep === stepIndex) {
    return true;
  }
  return context.activeStep !== null && context.activeStep >= stepIndex;
}

/** Remount key so preview (end frame) and play (frame 0) never share one DOM subtree. */
export function getHowItWorksPlaybackInstanceKey(shouldPlay: boolean, playbackGeneration: number): string {
  return shouldPlay ? `play-${playbackGeneration}` : `preview-${playbackGeneration}`;
}
export function useHowItWorksPlaybackInstanceKey(stepIndex) {
  const $ = _c(3);
  const shouldPlay = useHowItWorksStepShouldPlay(stepIndex);
  const playbackGeneration = useHowItWorksPlaybackGeneration(stepIndex);
  let t0;
  if ($[0] !== playbackGeneration || $[1] !== shouldPlay) {
    t0 = getHowItWorksPlaybackInstanceKey(shouldPlay, playbackGeneration);
    $[0] = playbackGeneration;
    $[1] = shouldPlay;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}